import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { toast } from 'react-semantic-toasts';
// graphql
import { useMutation, useQuery } from '@apollo/client';
import { ErrorHandler, captureException } from '@playbookux/playbook-web-core';
import {
  VERIFY_TEST_SESSION_TIME_PAST_AND_ALLOW_REFUND,
  UPDATE_TEST_SESSION_DID_NOT_SHOW,
} from '../../../../../core/graphql/test-sessions';
// errors handlers
// styles
import {
  Modal,
  ContainerContent,
  Button, ButtonInverted, Checkbox, CheckboxContent, ContainerButton, ContainerCheckbox, ContainerTextSize, Header, TextSecondary,
  LoaderContainer,
  Loader,
} from './styles';
import { TEST_SESSION_BOOKING_STATUS } from '../../../../../core/constants/test-session-status';

export const ParticipantAction = {
  ADD_CREDIT_PLAYBOOK: 'add_credits_to_workspace',
  SCHEDULE_NEW_PARTICIPANT: 'schedule_new_participant',
};

const BEFORETIMETEXT = 'You can\'t mark a participant as did not show until \n 5 minutes after the session start time.';
const DONEXTTEXT = 'What should we do next?';
const FAQ_LINK = 'http://help.playbookux.com/en/articles/4430929-moderated-interview-faqs';
// eslint-disable-next-line max-len
const NEWPARTICIPANTTEXT = 'A new participant will schedule time for this project. Please make sure your calendar is up to date before selecting this option.';
const RETURNCREDITTEXT = 'We’ll return the credits to your PlaybookUX workspace and they will be applied to future studies.';

const ParticipantDidNotShowModal = (props) => {
  const {
    openModal,
    onClose,
    firstName,
    testSessionId,
    price,
  } = props;

  const [selectParticipantAction, setSelectParticipantAction] = useState(null);
  const [isAllowedRefund, setIsAllowedRefund] = useState(null);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [isTimePast, setIsTimePast] = useState(null);

  const handleClose = () => {
    onClose();
    setSelectParticipantAction(null);
  };

  const [
    updateTestSessionDidNotShow,
    { loading: mutationLoading, error },
  ] = useMutation(UPDATE_TEST_SESSION_DID_NOT_SHOW);

  const { data, loading: queryLoading } = useQuery(VERIFY_TEST_SESSION_TIME_PAST_AND_ALLOW_REFUND, {
    variables: { testSessionId },
  });
  const isLoading = queryLoading || mutationLoading;

  const handleUpdateTestSessionDidNotShow = async () => {
    if (data.testSessions[0].bookingStatus === 'did_not_show') {
      toast({
        type: 'error',
        description: () => (
          <p>
            Tester is not allowed to refund.
          </p>
        ),
        time: 25000,
      });

      handleClose();
    } else if (testSessionId && selectParticipantAction) {
      try {
        await updateTestSessionDidNotShow({
          variables: {
            id: testSessionId,
            attributes: {
              option: selectParticipantAction,
            },
          },
        });
      } catch (e) {
        captureException('HANDLE_UPDATE_TEST_SESSION_DID_NOT_SHOW_FAILED', e);
      }

      if (!error) {
        if (selectParticipantAction === ParticipantAction.ADD_CREDIT_PLAYBOOK) {
          const amount = (price / 100).toFixed(2);
          toast({
            description: () => (
              <>
                <p>
                  We’ve credited your account
                  $
                  {amount}
                  USD
                </p>
              </>
            ),
            time: 25000,
          });
        }
        if (!isLoading) handleClose();
      }
    }
  };

  useEffect(() => {
    if (data && !error) {
      const isAllowed = data.testSessions[0].tester.project.allowRefund;
      setIsAllowedRefund(isAllowed);
      setIsTimePast(data.verifyTestSessionTimePast);
      setBookingStatus(data.testSessions[0].bookingStatus);
      if (!isAllowed) {
        setSelectParticipantAction(ParticipantAction.SCHEDULE_NEW_PARTICIPANT);
      }
    }
  }, [data, error]);

  const renderModal = () => {
    if (!isTimePast) {
      return (
        <>
          <ContainerTextSize>
            {BEFORETIMETEXT}
          </ContainerTextSize>

          <ContainerButton oneButton>
            <Button
              onClick={handleClose}
              loading={isLoading}
              oneButton
            >
              OK
            </Button>
          </ContainerButton>
        </>
      );
    } if (bookingStatus === TEST_SESSION_BOOKING_STATUS.interviewCompleted.value) {
      return (
        <>
          <TextSecondary>
            You are unable to mark
            {' '}
            {firstName}
            {' '}
            as “did not show” - all participants must be marked “did not show” within 7 days of the interview date and time.
          </TextSecondary>

          <TextSecondary>
            For more information, review our testing policies and FAQs
            {' '}
            <a href={FAQ_LINK}>here</a>
            .
          </TextSecondary>

          <ContainerButton oneButton>
            <Button
              onClick={handleClose}
              loading={isLoading}
              oneButton
            >
              OK
            </Button>
          </ContainerButton>
        </>
      );
    } if (bookingStatus === TEST_SESSION_BOOKING_STATUS.researcherDidNotSHow.value) {
      return (
        <>
          <TextSecondary>
            The researcher did not attend the session so you cannot mark
            {' '}
            {firstName}
            {' '}
            as “did not show.”
          </TextSecondary>
          <TextSecondary>
            For more information, review our testing policies and FAQs
            {' '}
            <a href={FAQ_LINK}>here</a>
            .
          </TextSecondary>

          <ContainerButton oneButton>
            <Button
              onClick={handleClose}
              loading={isLoading}
              oneButton
            >
              OK
            </Button>
          </ContainerButton>
        </>
      );
    } if (isAllowedRefund) {
      return (
        <>
          <Header>
            {`We’re sorry that ${firstName.trim()} did not show`}
          </Header>

          <TextSecondary>
            {DONEXTTEXT}
          </TextSecondary>

          <ContainerCheckbox>

            <CheckboxContent>
              <Checkbox
                radio
                label="Schedule a new participant as a replacement"
                value={ParticipantAction.SCHEDULE_NEW_PARTICIPANT}
                checked={selectParticipantAction === ParticipantAction.SCHEDULE_NEW_PARTICIPANT}
                onChange={() => setSelectParticipantAction(ParticipantAction.SCHEDULE_NEW_PARTICIPANT)}
              />
              <TextSecondary small>
                {NEWPARTICIPANTTEXT}
              </TextSecondary>
            </CheckboxContent>

            <CheckboxContent>
              <Checkbox
                radio
                label="Add the credits to my PlaybookUX workspace"
                value={ParticipantAction.ADD_CREDIT_PLAYBOOK}
                checked={selectParticipantAction === ParticipantAction.ADD_CREDIT_PLAYBOOK}
                onChange={() => setSelectParticipantAction(ParticipantAction.ADD_CREDIT_PLAYBOOK)}
              />
              <TextSecondary small>
                {RETURNCREDITTEXT}
              </TextSecondary>
            </CheckboxContent>

          </ContainerCheckbox>

          <ContainerButton>

            <ButtonInverted onClick={handleClose}>
              CANCEL
            </ButtonInverted>

            <Button
              onClick={handleUpdateTestSessionDidNotShow}
              loading={mutationLoading}
              disabled={_.isEmpty(selectParticipantAction) || mutationLoading}
            >
              SAVE
            </Button>

          </ContainerButton>
        </>
      );
    }

    if (!isAllowedRefund) {
      return (
        <>
          <ContainerTextSize>
            {`Do you want to mark ${firstName.trim()} as did not show?`}
          </ContainerTextSize>
          <ContainerButton>

            <ButtonInverted onClick={handleClose}>
              No
            </ButtonInverted>

            <Button
              onClick={handleUpdateTestSessionDidNotShow}
              loading={mutationLoading}
              disabled={mutationLoading}
            >
              Yes
            </Button>
          </ContainerButton>
        </>
      );
    }

    return <></>;
  };

  return (
    <Modal
      open={openModal}
      size="tiny"
      onClose={handleClose}
    >
      <ContainerContent>

        {error && (
          <ErrorHandler
            error={error}
            defaultMessage="Something went wrong while updating participant."
          />
        )}

        {isLoading && (
          <LoaderContainer>
            <Loader data-loader="participant-did-not-show-loader" />
          </LoaderContainer>
        )}

        {!isLoading && renderModal()}

      </ContainerContent>

    </Modal>
  );
};

ParticipantDidNotShowModal.propTypes = {
  openModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  firstName: PropTypes.string.isRequired,
  testSessionId: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
};

export default ParticipantDidNotShowModal;
